import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, X } from 'lucide-react';

// Authenticated thumbnail: fetches a protected image as a blob (Bearer header) so it renders
// in the browser instead of showing a broken-image icon. Click opens full size. Retry on error.
export const AuthImage = ({ src, token, alt = '', caption = '', className = '', testId }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(false);

  const fetchImg = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(src, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      setUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [src, token]);

  useEffect(() => { fetchImg(); return () => { setUrl((p) => { if (p) URL.revokeObjectURL(p); return null; }); }; }, [fetchImg]);

  if (error) {
    return (
      <button type="button" onClick={fetchImg} data-testid={testId ? `${testId}-retry` : undefined}
        className={`flex flex-col items-center justify-center gap-1 bg-red-50 border border-red-200 text-red-600 text-[10px] ${className}`}>
        <AlertTriangle size={16} /> <span>Failed</span> <span className="inline-flex items-center gap-0.5"><RefreshCw size={10} /> Retry</span>
      </button>
    );
  }
  if (loading || !url) {
    return <div className={`animate-pulse bg-slate-100 border border-slate-200 ${className}`} />;
  }
  return (
    <>
      <img src={url} alt={alt} title={caption} onClick={() => setZoom(true)} data-testid={testId}
        className={`cursor-zoom-in object-cover ${className}`} />
      {zoom && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6" onClick={() => setZoom(false)} data-testid={testId ? `${testId}-full` : undefined}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setZoom(false)}><X size={28} /></button>
          <img src={url} alt={alt} className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl" />
          {caption && <div className="absolute bottom-6 text-white text-sm bg-black/50 px-3 py-1 rounded">{caption}</div>}
        </div>
      )}
    </>
  );
};

export default AuthImage;
